import { type FormEvent, useEffect, useState } from 'react'
import AddIcon from '@mui/icons-material/Add'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteIcon from '@mui/icons-material/Delete'
import RefreshIcon from '@mui/icons-material/Refresh'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { QRCode } from 'react-qr-code'

import { GenericHttpError } from 'common/http/httpError'
import { ErrorCode } from 'common/errors'
import { passkeysSupported } from '../passkeys'
import { isReauthenticationRequired, ReauthenticationForm } from './ReauthenticationForm'

export interface TotpEnrollmentData {
    enrollmentId: string
    secret: string
    uri: string
    expiresAt: Date
}

export interface TotpManagerApi {
    beginEnrollment: () => Promise<TotpEnrollmentData>
    completeEnrollment: (enrollmentId: string, code: string) => Promise<unknown>
    remove: () => Promise<unknown>
    reauthenticate: (password: string) => Promise<void>
}

export interface TotpManagerProps {
    api: TotpManagerApi
    totpEnabled: boolean
    mfaEnforced?: boolean
    onChanged: () => Promise<void> | void
}

const errorDetail = (error: unknown, fallback: string) => {
    if (!(error instanceof GenericHttpError) || typeof error.body === 'string') return fallback
    if (typeof error.body.detail !== 'string' || !error.body.detail) return fallback
    return error.body.detail
}

export const totpLoginError = (error: unknown, fallback = 'Invalid code') => {
    if (
        error instanceof GenericHttpError &&
        typeof error.body !== 'string' &&
        error.body.type === ErrorCode.tooManyLoginAttempts
    ) {
        return 'Too many login attempts. Please try again later.'
    }
    return fallback
}

const CodeField = ({
    value,
    onChange,
    autoFocus = false,
}: {
    value: string
    onChange: (code: string) => void
    autoFocus?: boolean
}) => (
    <TextField
        autoFocus={autoFocus}
        fullWidth
        label="6-digit code"
        value={value}
        onChange={(event) => onChange(event.target.value.replace(/\D/g, '').slice(0, 6))}
        autoComplete="one-time-code"
        slotProps={{ htmlInput: { inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6 } }}
    />
)

const ButtonFeedback = ({ busy, label }: { busy: boolean; label: string }) => {
    if (busy) return <CircularProgress size={20} />
    return label
}

const SetupContent = ({ enrollment }: { enrollment: TotpEnrollmentData }) => {
    const [copied, setCopied] = useState(false)
    let copyIcon = <ContentCopyIcon />
    let copyLabel = 'Copy'
    if (copied) {
        copyIcon = <CheckCircleIcon />
        copyLabel = 'Copied'
    }
    return (
        <Stack spacing={2} sx={{ alignItems: 'center' }}>
            <Box sx={{ bgcolor: 'white', p: 2, borderRadius: 2, lineHeight: 0 }}>
                <QRCode value={enrollment.uri} size={184} aria-label="Authenticator QR code" />
            </Box>
            <Box sx={{ width: '100%' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Can’t scan the code? Enter this setup key manually.
                </Typography>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <Box
                        component="code"
                        sx={{ flex: 1, p: 1.25, borderRadius: 1, bgcolor: 'action.hover', overflowWrap: 'anywhere' }}
                    >
                        {enrollment.secret}
                    </Box>
                    <Button
                        aria-label="Copy setup key"
                        startIcon={copyIcon}
                        onClick={() => {
                            void navigator.clipboard.writeText(enrollment.secret)
                            setCopied(true)
                        }}
                    >
                        {copyLabel}
                    </Button>
                </Stack>
            </Box>
        </Stack>
    )
}

export const TotpManager = ({ api, totpEnabled, mfaEnforced, onChanged }: TotpManagerProps) => {
    const [action, setAction] = useState<'enroll' | 'remove'>()
    const [reauthenticationRequired, setReauthenticationRequired] = useState(false)
    const [reauthenticating, setReauthenticating] = useState(false)
    const [dialogOpen, setDialogOpen] = useState(false)
    const [enrollment, setEnrollment] = useState<TotpEnrollmentData>()
    const [code, setCode] = useState('')
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string>()

    const reset = () => {
        setAction(undefined)
        setReauthenticationRequired(false)
        setEnrollment(undefined)
        setCode('')
        setError(undefined)
    }
    const close = () => {
        if (!busy && !reauthenticating) setDialogOpen(false)
    }

    const startEnrollment = async () => {
        if (busy) return
        setAction('enroll')
        setDialogOpen(true)
        setEnrollment(undefined)
        setCode('')
        setError(undefined)
        setBusy(true)
        try {
            setEnrollment(await api.beginEnrollment())
        } catch (error) {
            if (isReauthenticationRequired(error)) {
                setReauthenticationRequired(true)
                return
            }
            setError(errorDetail(error, 'Authenticator setup could not be started. Please try again.'))
        } finally {
            setBusy(false)
        }
    }

    const remove = async () => {
        if (busy) return
        setBusy(true)
        setError(undefined)
        try {
            await api.remove()
            await onChanged()
            setDialogOpen(false)
        } catch (error) {
            if (isReauthenticationRequired(error)) {
                setReauthenticationRequired(true)
                return
            }
            setError(errorDetail(error, 'The authenticator app could not be removed. Please try again.'))
        } finally {
            setBusy(false)
        }
    }

    const completeEnrollment = async (event: FormEvent) => {
        event.preventDefault()
        if (!enrollment || code.length !== 6 || busy) return
        setBusy(true)
        setError(undefined)
        try {
            await api.completeEnrollment(enrollment.enrollmentId, code)
            await onChanged()
            setDialogOpen(false)
        } catch (error) {
            if (isReauthenticationRequired(error)) {
                setReauthenticationRequired(true)
                return
            }
            setError('That code is invalid. Wait for a new code and try again.')
        } finally {
            setBusy(false)
        }
    }

    let statusDescription = 'Add a time-based verification code as a second sign-in step.'
    let setupButtonVariant: 'contained' | 'outlined' = 'contained'
    let setupButtonIcon = <AddIcon />
    let setupButtonLabel = 'Set up'
    let enabledChip
    if (totpEnabled) {
        statusDescription = 'Password sign-ins require a time-based verification code.'
        setupButtonVariant = 'outlined'
        setupButtonIcon = <RefreshIcon />
        setupButtonLabel = 'Replace'
        enabledChip = <Chip label="Enabled" color="success" size="small" />
    }

    const startRemoval = () => {
        setAction('remove')
        setDialogOpen(true)
    }

    let dialogContent = (
        <>
            <DialogTitle>Remove authenticator app?</DialogTitle>
            <DialogContent>
                <DialogContentText>You will no longer enter authenticator codes when signing in.</DialogContentText>
                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={close} disabled={busy}>
                    Cancel
                </Button>
                <Button color="error" variant="contained" disabled={busy} onClick={() => void remove()}>
                    <ButtonFeedback busy={busy} label="Remove" />
                </Button>
            </DialogActions>
        </>
    )
    if (action === 'enroll') {
        let enrollmentContent = (
            <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 120 }}>
                <CircularProgress aria-label="Preparing authenticator setup" />
            </Box>
        )
        let enrollmentActions = (
            <Button onClick={close} disabled={busy}>
                Cancel
            </Button>
        )
        if (error) {
            enrollmentContent = <Alert severity="error">{error}</Alert>
        }
        if (enrollment) {
            let enrollmentInstructions = 'Scan the QR-code and enter the code shown by your authenticator app.'
            if (totpEnabled) {
                enrollmentInstructions += ' Your existing authenticator remains active until this step succeeds.'
            }
            enrollmentContent = (
                <>
                    <DialogContentText sx={{ mb: 2 }}>{enrollmentInstructions}</DialogContentText>
                    <SetupContent enrollment={enrollment} />
                    <Box sx={{ mt: 2 }}>
                        <CodeField value={code} onChange={setCode} autoFocus />
                    </Box>
                    {error && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {error}
                        </Alert>
                    )}
                </>
            )
            enrollmentActions = (
                <>
                    <Button onClick={close} disabled={busy}>
                        Cancel
                    </Button>
                    <Button type="submit" variant="contained" disabled={code.length !== 6 || busy}>
                        <ButtonFeedback busy={busy} label="Verify and enable" />
                    </Button>
                </>
            )
        }
        dialogContent = (
            <Box component="form" onSubmit={(event) => void completeEnrollment(event)}>
                <DialogTitle>Connect your authenticator app</DialogTitle>
                <DialogContent>{enrollmentContent}</DialogContent>
                <DialogActions>{enrollmentActions}</DialogActions>
            </Box>
        )
    }

    if (reauthenticationRequired) {
        dialogContent = (
            <ReauthenticationForm
                reauthenticate={api.reauthenticate}
                onBusyChange={setReauthenticating}
                onCancel={close}
                onAuthenticated={async () => {
                    setReauthenticationRequired(false)
                    // Reauthentication rotates the session and invalidates its pending enrollment.
                    if (action === 'enroll') await startEnrollment()
                    else await remove()
                }}
            />
        )
    }

    return (
        <Paper sx={{ p: 2 }}>
            <Stack direction="row" spacing={2} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <Typography variant="h2">Authenticator app</Typography>
                        {enabledChip}
                    </Stack>
                    <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                        {statusDescription}
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                    <Button
                        variant={setupButtonVariant}
                        startIcon={setupButtonIcon}
                        onClick={() => void startEnrollment()}
                    >
                        {setupButtonLabel}
                    </Button>
                    {totpEnabled && mfaEnforced === false && (
                        <Button color="error" startIcon={<DeleteIcon />} onClick={startRemoval}>
                            Remove
                        </Button>
                    )}
                </Stack>
            </Stack>

            <Dialog open={dialogOpen} onClose={close} onTransitionExited={reset} maxWidth="sm" fullWidth>
                {dialogContent}
            </Dialog>
        </Paper>
    )
}

export interface TotpLoginChallengeData {
    state: 'totpRequired' | 'totpEnrollmentRequired'
    challengeId: string
    passkeyAvailable: boolean
}

export interface TotpLoginApi<TResult> {
    complete: (challengeId: string, code: string) => Promise<TResult>
    beginEnrollment: (challengeId: string) => Promise<TotpEnrollmentData>
    completeEnrollment: (enrollmentId: string, code: string) => Promise<TResult>
    abandon: (challengeId: string) => Promise<void>
}

export const TotpLoginDialog = <TResult,>({
    challenge,
    api,
    onAuthenticated,
    onCancel,
    onUsePasskey,
}: {
    challenge: TotpLoginChallengeData
    api: TotpLoginApi<TResult>
    onAuthenticated: (result: TResult) => void | Promise<void>
    onCancel: () => void
    onUsePasskey?: () => Promise<void>
}) => {
    const [enrollment, setEnrollment] = useState<TotpEnrollmentData>()
    const [code, setCode] = useState('')
    const [busy, setBusy] = useState(challenge.state === 'totpEnrollmentRequired')
    const [error, setError] = useState<string>()

    useEffect(() => {
        if (challenge.state !== 'totpEnrollmentRequired') return
        let active = true
        api.beginEnrollment(challenge.challengeId)
            .then((value) => active && setEnrollment(value))
            .catch(() => active && setError('Authenticator setup could not be started. Please sign in again.'))
            .finally(() => active && setBusy(false))
        return () => {
            active = false
        }
    }, [api, challenge])

    const cancel = () => {
        if (busy) return
        void api.abandon(challenge.challengeId).finally(onCancel)
    }

    const submit = async (event: FormEvent) => {
        event.preventDefault()
        if (code.length !== 6 || busy) return
        setBusy(true)
        setError(undefined)
        try {
            let result: TResult
            if (enrollment) result = await api.completeEnrollment(enrollment.enrollmentId, code)
            else result = await api.complete(challenge.challengeId, code)
            await onAuthenticated(result)
        } catch (error) {
            setError(totpLoginError(error, 'Invalid code. Wait for a new code and try again.'))
            setBusy(false)
        }
    }

    const handlePasskey = async () => {
        if (!onUsePasskey || busy) return
        setBusy(true)
        setError(undefined)
        try {
            await onUsePasskey()
        } catch {
            setError('Passkey sign in failed. You can still use your authenticator code.')
            setBusy(false)
        }
    }

    let title = 'Enter your verification code'
    if (challenge.state === 'totpEnrollmentRequired') title = 'Preparing authenticator setup…'
    if (enrollment) title = 'Setup MFA'

    let instructions = (
        <DialogContentText sx={{ mb: 2 }}>
            Open your authenticator app and enter the current 6-digit code.
        </DialogContentText>
    )
    if (challenge.state === 'totpEnrollmentRequired') {
        instructions = <DialogContentText sx={{ mb: 2 }}>Creating a secure setup key…</DialogContentText>
    }
    if (enrollment) {
        instructions = (
            <>
                <DialogContentText sx={{ mb: 2 }}>
                    MFA is required for your account. Scan the QR-code and enter the code shown by your authenticator
                    app.
                </DialogContentText>
                <SetupContent enrollment={enrollment} />
            </>
        )
    }

    let codeMargin = 0
    let submitLabel = 'Verify'
    if (enrollment) {
        codeMargin = 2
        submitLabel = 'Enable and sign in'
    }

    return (
        <Dialog open maxWidth="sm" fullWidth aria-labelledby="totp-login-title">
            <Box component="form" onSubmit={(event) => void submit(event)}>
                <DialogTitle id="totp-login-title">{title}</DialogTitle>
                <DialogContent>
                    {instructions}
                    {(enrollment || challenge.state === 'totpRequired') && (
                        <Box sx={{ mt: codeMargin }}>
                            <CodeField value={code} onChange={setCode} autoFocus />
                        </Box>
                    )}
                    {busy && !enrollment && (
                        <Box sx={{ display: 'grid', placeItems: 'center', py: 2 }}>
                            <CircularProgress />
                        </Box>
                    )}
                    {error && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {error}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={cancel} disabled={busy}>
                        Cancel
                    </Button>
                    {challenge.passkeyAvailable && onUsePasskey && passkeysSupported() && (
                        <Button onClick={() => void handlePasskey()} disabled={busy}>
                            Use a passkey instead
                        </Button>
                    )}
                    <Button type="submit" variant="contained" disabled={code.length !== 6 || busy}>
                        <ButtonFeedback busy={busy} label={submitLabel} />
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    )
}
